import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Archive as ArchiveIcon, Search, Users, FileText, Wrench, ArrowLeft, RotateCcw,
  Calendar, DollarSign,
} from "lucide-react";
import { GlassPanel } from "../components/GlassPanel";
import { CustomerVehiclePhoto } from "../components/DetailsModal";
import { relativeTime, absoluteTime } from "../lib/utils";
import { subMeta } from "../lib/pipeline";

// The Archive page shows every record whose main_status is "archived",
// grouped by what they were before they got archived (job / quote / lead).
// Records are inferred from the terminal sub_status:
//   completed / collected / delivered → archived jobs
//   rejected                          → failed quotes
//   lost / unqualified                → rejected leads
// Exported so the Quotes tab can run the same classification locally
// when it surfaces archive matches alongside live-quote search results.
export const ARCHIVE_GROUPS = [
  { key: "jobs",   label: "Completed jobs",  icon: Wrench,   subs: new Set(["completed", "collected", "delivered"]) },
  { key: "quotes", label: "Failed quotes",   icon: FileText, subs: new Set(["rejected", "expired", "auto_expired_quote"]) },
  { key: "leads",  label: "Rejected leads",  icon: Users,    subs: new Set(["lost", "unqualified", "auto_expired_lead"]) },
];

// Match an archive row's `contact_name / vehicle / service` fields against
// a free-text search needle. Shared so the Quotes-tab pill and the
// Archive page agree on which rows count as "a match".
export function matchesArchiveQuery(row, needle) {
  if (!needle) return true;
  const t = needle.trim().toLowerCase();
  if (!t) return true;
  return (
    (row.contact_name || "").toLowerCase().includes(t) ||
    (row.vehicle || "").toLowerCase().includes(t) ||
    (row.service || "").toLowerCase().includes(t)
  );
}

export default function ArchivePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Honour `?group=` and `?q=` from the URL so the Quotes tab can
  // deep-link straight into the matching archive bucket pre-filtered
  // by the same search needle. Falls back to the usual defaults.
  const [searchParams, setSearchParams] = useSearchParams();
  const [group, setGroup] = useState(() => {
    const g = searchParams.get("group");
    return ARCHIVE_GROUPS.some((x) => x.key === g) ? g : "jobs";
  });
  const [q, setQ] = useState(() => searchParams.get("q") || "");

  // Keep the URL in sync with the active tab + search needle. React
  // Router treats missing params as "remove from URL" so we only set
  // the keys that actually have a value — avoids ?q= in the bar.
  useEffect(() => {
    const next = new URLSearchParams();
    if (group && group !== "jobs") next.set("group", group);
    if (q.trim()) next.set("q", q.trim());
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, q]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs?main_status=archived");
      // Newest first — the archive is a firehose, so sort by archived timestamp
      const sorted = [...(data || [])].sort((a, b) => {
        const at = new Date(a.archived_at || a.updated_at || a.created_at || 0).getTime();
        const bt = new Date(b.archived_at || b.updated_at || b.created_at || 0).getTime();
        return bt - at;
      });
      setRows(sorted);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load archive");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const groupBuckets = useMemo(() => {
    const buckets = { jobs: [], quotes: [], leads: [], other: [] };
    for (const r of rows) {
      const sub = (r.sub_status || "").toLowerCase();
      const hit = ARCHIVE_GROUPS.find((g) => g.subs.has(sub));
      if (hit) buckets[hit.key].push(r);
      else buckets.other.push(r);
    }
    return buckets;
  }, [rows]);

  const visible = useMemo(() => {
    const list = groupBuckets[group] || [];
    if (!q.trim()) return list;
    const t = q.trim().toLowerCase();
    return list.filter((r) => (
      (r.contact_name || "").toLowerCase().includes(t) ||
      (r.vehicle || "").toLowerCase().includes(t) ||
      (r.service || "").toLowerCase().includes(t)
    ));
  }, [groupBuckets, group, q]);

  const restore = async (row) => {
    // Best-guess reverse — send it back to the stage it came from based on
    // the archived sub_status. Users can move it further via the normal
    // pipeline once restored.
    const target = restoreTargetFor(row);
    if (!window.confirm(`Restore ${row.contact_name || row.vehicle || "this record"} to ${target.main_status}?`)) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${row.id}/advance`, target);
      toast.success(`Restored to ${target.main_status}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally {
      setBusy(false);
    }
  };

  const activeGroup = ARCHIVE_GROUPS.find((g) => g.key === group);
  const totalCounts = ARCHIVE_GROUPS.map((g) => ({
    ...g, count: (groupBuckets[g.key] || []).length,
  }));

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-24" data-testid="archive-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-label flex items-center gap-2">
            <ArchiveIcon className="w-3 h-3" /> History
          </div>
          <h1 className="font-heading text-3xl text-white mt-1">Archive</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Completed jobs, failed quotes, and rejected leads all live here. Restore any record back to the pipeline with one click.
          </p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="archive-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, vehicle, or service…"
            className="bg-black/40 border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30 w-72"
          />
        </div>
      </div>

      {/* Group tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {totalCounts.map((g) => {
          const Icon = g.icon;
          const active = group === g.key;
          return (
            <button
              key={g.key}
              type="button"
              data-testid={`archive-tab-${g.key}`}
              onClick={() => setGroup(g.key)}
              className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border transition ${
                active
                  ? "bg-white text-black border-white font-semibold"
                  : "bg-white/[0.02] border-white/10 text-slate-300 hover:text-white hover:border-white/20"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {g.label}
              <span className={`ml-1 text-[10px] ${active ? "text-black/60" : "text-slate-500"}`}>
                ({g.count})
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : visible.length === 0 ? (
        <GlassPanel className="!p-10 text-center">
          <activeGroup.icon className="w-8 h-8 mx-auto text-slate-500 mb-2" />
          <div className="text-slate-300 text-sm">
            {q.trim() ? "Nothing matches your search." : `No ${(activeGroup.label || "").toLowerCase()} yet.`}
          </div>
        </GlassPanel>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {visible.map((r) => (
            <ArchiveCard key={r.id} row={r} onRestore={() => restore(r)} disabled={busy} />
          ))}
        </div>
      )}
    </div>
  );
}

function ArchiveCard({ row, onRestore, disabled }) {
  const meta = subMeta(row.sub_status);
  const Icon = meta?.icon;
  return (
    <GlassPanel className="!p-4 flex flex-col gap-3" data-testid={`archive-card-${row.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`pill ${meta?.pill || "pill-muted"} text-[10px] inline-flex items-center gap-1`}>
              {Icon && <Icon className="w-2.5 h-2.5" />}
              {meta?.label || row.sub_status}
            </span>
            {row.archived_at && (
              <span
                className="text-[10px] uppercase tracking-widest text-slate-500"
                title={absoluteTime(row.archived_at)}
              >
                {relativeTime(row.archived_at)}
              </span>
            )}
          </div>
          <div className="mt-2 text-white font-medium truncate">
            {row.vehicle || row.service || "Untitled"}
          </div>
          <div className="text-xs text-slate-400 truncate">{row.contact_name}</div>
        </div>
        {row.photo_path && <CustomerVehiclePhoto path={row.photo_path} size="sm" />}
      </div>

      <div className="text-[11px] text-slate-500 space-y-1">
        {row.scheduled_date && (
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3" /> {row.scheduled_date}
          </div>
        )}
        {(row.amount || row.amount_override) > 0 && (
          <div className="flex items-center gap-1.5">
            <DollarSign className="w-3 h-3" />
            ${Number(row.amount_override || row.amount).toLocaleString()}
          </div>
        )}
      </div>

      <div className="pt-2 mt-auto border-t border-white/5 flex items-center gap-2">
        <button
          type="button"
          data-testid={`archive-restore-${row.id}`}
          disabled={disabled}
          onClick={onRestore}
          className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
        >
          <RotateCcw className="w-3 h-3" /> Restore
        </button>
      </div>
    </GlassPanel>
  );
}

// Best-guess restoration target based on the archived sub_status. Users
// can move it further along the pipeline once the record is back.
function restoreTargetFor(row) {
  const sub = (row.sub_status || "").toLowerCase();
  if (["lost", "unqualified", "auto_expired_lead"].includes(sub)) {
    return { main_status: "lead", sub_status: "new" };
  }
  if (["rejected", "expired", "auto_expired_quote"].includes(sub)) {
    return { main_status: "quote", sub_status: "draft" };
  }
  // Everything else = a finished job → restore to "collected" so office
  // decides whether to keep it in Jobs or re-open a stage.
  return { main_status: "job", sub_status: "collected" };
}
